/**
 * "How would this money be invested?" — asked where the money actually is.
 *
 * OWNER, 2026-08-28. This used to be a row at the top of Step 5 of the fact find: "If a strategy
 * frees up money, how would it be invested?" Their objection was that it introduces a concept that
 * may never apply, and they were right — the codebase had already written down the same rule in
 * three places ("asked just-in-time, never a mandatory fact-find step") and then asked it upfront
 * anyway.
 *
 * Two things were wrong with asking it there, and the second is the one that mattered:
 *
 *   1. IT IS UNANSWERABLE IN THE ABSTRACT. At fact-find time there is no strategy and no figure, so
 *      "if a strategy frees up money" asks the client to price a hypothetical.
 *   2. ANSWERING IT EARLY SILENCED THE BETTER QUESTION. Every module's just-in-time ask is gated on
 *      `!hasAnsweredNewMoneyProfile(factFind)`. A client who picked something at Step 5 to get past
 *      it was then never asked in context — where the prompt names the money it is about. The vague
 *      question did not merely sit unused; it switched off the good one.
 *
 * Removing it alone would have opened a real gap, which is why this component exists. MANUAL CONTROL
 * uses this profile but never asked for it: the allocators price allocated shares at it and show it
 * as a bare word on a button ("Invest the remainder — Conservative"), unexplained and unchangeable.
 * The only place a manual-only client could have changed it was the Step 5 row being removed.
 *
 * So the question now appears in both modes, and in both it appears next to a number: the engine
 * asks before it ranks, and this asks beside the allocation it prices.
 */
import React from 'react';
import { FormSelect } from '../ui/index.tsx';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import {
    INVESTMENT_PROFILES,
    INVESTMENT_PROFILE_IDS,
    newMoneyProfileOf,
    hasAnsweredNewMoneyProfile,
} from '../../../../shared/src/calculation/investmentProfiles.ts';

/*
 * THE UNANSWERED DEFAULT IS NOT `DEFAULT_INVESTMENT_PROFILE`. That constant is Balanced and belongs
 * to CLIENT-ENTERED super accounts, because Balanced is what most funds actually default to. Money
 * the TOOL proposes defaults to Conservative instead (NEW_MONEY_DEFAULT_PROFILE — an adviser
 * ruling), and a firm may override it again with its own capital market assumptions.
 *
 * Naming either constant here would have printed a word the engine disagrees with — the same
 * two-figures-one-thing shape this codebase keeps meeting. Asking the engine's own resolver with no
 * answer recorded returns exactly what it will use, firm override included.
 */
const unansweredDefault = () => newMoneyProfileOf(undefined);

interface NewMoneyProfileFieldProps {
    factFind: DraftFactFind;
    /** Absent → the control renders read-only: it can still SAY what is assumed, but not change it. */
    setFactFind?: React.Dispatch<React.SetStateAction<DraftFactFind>>;
    /** Keeps the DOM id unique where two allocators sit on one page. */
    idPrefix?: string;
    /** Names the money this prices, e.g. "the money you invest here". */
    what?: string;
}

/**
 * The investment approach applied to money a strategy puts to work. Writes
 * `goals.newMoneyInvestmentProfile`, the same field the Suggestion Engine's own gate writes — so
 * answering it here means the engine does not ask again, which is the intended behaviour: it has
 * been answered, in context, with a figure on screen.
 */
export function NewMoneyProfileField({ factFind, setFactFind, idPrefix = 'deploy', what = 'this money' }: NewMoneyProfileFieldProps) {
    const answered = hasAnsweredNewMoneyProfile(factFind);
    const current = newMoneyProfileOf(factFind);
    const fallback = unansweredDefault();
    const id = `${idPrefix}-new-money-profile`;

    const choose = (v: string) => {
        if (!setFactFind) return;
        setFactFind((prev) => {
            const goals = { ...(prev.goals || {}) } as Record<string, unknown>;
            if (v) goals.newMoneyInvestmentProfile = v;
            else delete goals.newMoneyInvestmentProfile;
            return { ...prev, goals };
        });
    };

    return (
        <div className="mt-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <label htmlFor={id} className="text-xs font-semibold text-slate-800 sm:flex-1">
                    How is {what} invested?
                </label>
                <div className="w-full sm:w-56">
                    {setFactFind ? (
                        <FormSelect
                            id={id}
                            name={id}
                            value={answered ? current : ''}
                            onChange={(_n: string, v: string) => choose(v)}
                        >
                            <option value="">{`Not set — assume ${INVESTMENT_PROFILES[fallback].label}`}</option>
                            {INVESTMENT_PROFILE_IDS.map((pid) => (
                                <option key={pid} value={pid}>{INVESTMENT_PROFILES[pid].label}</option>
                            ))}
                        </FormSelect>
                    ) : (
                        <p className="text-xs font-semibold text-slate-900">{INVESTMENT_PROFILES[current].label}</p>
                    )}
                </div>
            </div>
            <p className="mt-1.5 text-xs text-slate-600">
                {answered ? (
                    <>
                        We&apos;ll project this money on a <strong>{INVESTMENT_PROFILES[current].label}</strong> approach —
                        here, and anywhere else a strategy invests for you.
                    </>
                ) : (
                    <>
                        Left blank we assume <strong>{INVESTMENT_PROFILES[fallback].label}</strong>, the most cautious
                        option, and say so wherever it applies. A higher-growth mix projects a bigger number, but the
                        year-to-year swings are larger and nothing is guaranteed.
                    </>
                )}
            </p>
        </div>
    );
}

export default NewMoneyProfileField;
